import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import Helmet from 'react-helmet';
import { withRouter } from 'react-router-dom';
import {
  compose,
  join,
  isNil,
  reject,
  defaultTo,
  multiply,
  subtract,
  add,
  path,
  eqProps,
  isEmpty,
} from 'ramda';
import { Row, Col, Spin } from 'antd';

import ReleaseCard from '../ReleaseCard/ReleaseCard';
import Pagination, { getReleaseList, getPageSize } from '../Pagination';
import SheetTitle from '../SheetTitle';
import { smoothScroll, isSSR, slugify } from '../../helpers/view';
import analytics from '../../helpers/analytics';
import { setPageNotFound } from '../../actions/app';
import BackButton from '../BackButton';
import Error from '../Error';
import LoadingContainer from '../LoadingContainer';

import c from './ReleaseList.less';

class ReleaseList extends Component {
  static propTypes = {
    match: PropTypes.object.isRequired,
    user: PropTypes.object.isRequired,
    app: PropTypes.object.isRequired,
    categoryItems: PropTypes.object.isRequired,
    artistList: PropTypes.object.isRequired,
    releaseList: PropTypes.object.isRequired,
    categoryReleaseList: PropTypes.object.isRequired,
    history: PropTypes.object.isRequired,
    location: PropTypes.object.isRequired,
    currentCategory: PropTypes.object,
    loadReleasesByCategory: PropTypes.func.isRequired,
    loadCategoryById: PropTypes.func.isRequired,
    setPageNotFound: PropTypes.func.isRequired,
    renderSubItems: PropTypes.func,
    title: PropTypes.string,
    description: PropTypes.string,
    pathPrefix: PropTypes.string,
    backToListTitle: PropTypes.string,
    currentCategoryLoading: PropTypes.bool.isRequired,
  };

  static defaultProps = {
    title: '',
    description: '',
    pathPrefix: '',
    backToListTitle: 'Back',
    renderSubItems: () => null,
    currentCategory: null,
  };

  UNSAFE_componentWillMount() {
    if (isSSR()) {
      const { match: { params: { slug } }, currentCategory, categoryItems } = this.props;

      if (this.pageNotFound || (!categoryItems.loading && categoryItems.error)) {
        this.props.staticContext.page.status = 404;
      }

      if (!currentCategory) {
        return null;
      }

      const correctSlug = slugify(currentCategory.name);

      if (correctSlug.length > 0 && slug !== correctSlug) {
        this.props.staticContext.page.status = 301;
        return this.props.history.replace(`/${this.props.pathPrefix}/${correctSlug}-${currentCategory.id}`);
      }
    }

    return null;
  }

  componentDidMount() {
    const { match: { params: { id, slug } }, categoryReleaseList: { ids } } = this.props;

    if (!['Genre', 'Series'].includes(this.props.app.renderedComponent)) {
      this.props.loadCategoryById(id);
    }

    if (!this.pageNotFound && isEmpty(ids)) {
      this._loadData(this.props);
    }

    if (this.props.currentCategory && this.props.currentCategory.parentId) {
      this.props.loadCategoryById(this.props.currentCategory.parentId);
    }

    smoothScroll();

    analytics.pageview(`/catalog/${this.props.pathPrefix}/${slug}-${id}`);
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (!eqProps('currentCategory', nextProps, this.props)) {
      const correctSlug = slugify(nextProps.currentCategory.name);

      if (correctSlug.length > 0 && nextProps.match.params.slug !== correctSlug) {
        this.props.history.replace(`/${this.props.pathPrefix}/${correctSlug}-${nextProps.currentCategory.id}`);
      }

      if (nextProps.currentCategory && nextProps.currentCategory.parentId) {
        this.props.loadCategoryById(nextProps.currentCategory.parentId);
      }
    }

    if (!eqProps('id', this.props.match.params, nextProps.match.params)) {
      this._loadData(nextProps);
    }

    if (!eqProps('page', this.props.match.params, nextProps.match.params)) {
      this._loadData(nextProps, path(['state', 'loadMore'], nextProps.location));
    }

    if (!eqProps('categoryReleaseList', nextProps, this.props) && !path(['state', 'loadMore'], nextProps.location)) {
      smoothScroll();
    }

    if (!eqProps('categoryReleaseList', nextProps, this.props)) {
      const { match: { params: { page } }, categoryReleaseList: { total } } = nextProps;

      if (total < (parseInt(page, 10) - 1) * getPageSize()) {
        this.props.setPageNotFound();
      }
    }

    return null;
  }

  componentDidUpdate(prevProps) {
    if (
      !eqProps('id', this.props.match.params, prevProps.match.params)
      || !eqProps('page', this.props.match.params, prevProps.match.params)
    ) {
      const { match: { params: { id, slug } } } = this.props;

      analytics.pageview(`/catalog/${this.props.pathPrefix}/${slug}-${id}`);
    }
  }

  get pageNotFound() {
    const { match: { params: { page } }, categoryReleaseList: { total } } = this.props;

    return (total < (parseInt(page, 10) - 1) * getPageSize());
  }

  handlePageChange = (page) => this._buildUrl(page);

  handleLoadMore = () => this._buildUrl(compose(
    add(1),
    defaultTo(1),
    path(['match', 'params', 'page'])
  )(this.props), true);

  _buildUrl = (page, loadMore = false) => {
    const { match: { params }, pathPrefix } = this.props;

    this.props.history.push({
      pathname: compose(
        join('/'),
        reject(isNil)
      )([
        '',
        pathPrefix,
        `${params.slug}-${params.id}`,
        page,
      ]),
      state: {
        loadMore,
      },
    });
  };

  _loadData = (props, loadMore) => {
    const { match: { params: { id, page } } } = props;

    this.props.loadReleasesByCategory(id, {
      limit: getPageSize(),
      offset: multiply(getPageSize(), subtract(defaultTo(1, page), 1)),
      concat: loadMore,
    });
  };

  renderBackButton() {
    const {
      currentCategory, pathPrefix, categoryItems, backToListTitle,
    } = this.props;
    let returnTo = `/${pathPrefix}`;
    let backTitle = backToListTitle;

    if (currentCategory && currentCategory.parentId && categoryItems.data[currentCategory.parentId]) {
      const parent = categoryItems.data[currentCategory.parentId];

      returnTo = `/${pathPrefix}/${slugify(parent.name, parent.id)}`;
      backTitle = parent.name;
    }

    return (<BackButton to={returnTo} title={backTitle} desktopHidden={false} />);
  }

  render() {
    const {
      title,
      description,
      categoryItems,
      currentCategory,
      releaseList,
      categoryReleaseList,
      match: {
        params: { page, slug, id },
      },
      pathPrefix,
      user,
      artistList,
      renderSubItems,
      currentCategoryLoading,
    } = this.props;

    if (
      (currentCategoryLoading && !currentCategory)
      || (categoryReleaseList.loading && this.pageNotFound)
    ) {
      return <LoadingContainer />;
    }

    if (
      (!currentCategoryLoading && !currentCategory)
      || (!categoryReleaseList.loading && this.pageNotFound)
      || (!categoryItems.loading && categoryItems.error)
    ) {
      return <Error errorCode="404" />;
    }

    return (
      <div className={c.container}>
        <Helmet defer={false}>
          <title>{title.replace('{category}', currentCategory.name)}</title>
          <meta name="description" content={description.replace('{category}', currentCategory.name)} />
          <meta property="og:title" content={title.replace('{category}', currentCategory.name)} />
          <meta property="og:description" content={description.replace('{category}', currentCategory.name)} />
        </Helmet>
        {this.renderBackButton()}
        <SheetTitle title={currentCategory.name} />
        {
          currentCategory.children && (
            <Row className={c.subseries} gutter={{ sm: 16, md: 20 }}>
              {
                currentCategory.children
                  .map((cid) => categoryItems.data[cid])
                  .map((item) => (
                    <Col key={item.id} xs={24} sm={12} md={8} lg={6}>
                      {renderSubItems(item)}
                    </Col>
                  ))
              }
            </Row>
          )
        }
        <Spin size="large" spinning={releaseList.loading}>
          <Row
            gutter={{
              xs: 10, sm: 16, md: 20, lg: 20,
            }}
            className={c.releaseListContainer}
          >
            {
              getReleaseList(categoryReleaseList.ids, releaseList, artistList)
                .map((release) => (
                  <Col xs={12} sm={12} md={8} lg={6} key={release.id}>
                    <ReleaseCard release={release} loggedId={user.loggedIn} />
                  </Col>
                ))
            }
          </Row>
        </Spin>
        <Pagination
          total={categoryReleaseList.total}
          currentPage={page}
          isLoading={releaseList.loading}
          onLoadMore={this.handleLoadMore}
          onPageChange={this.handlePageChange}
          baseHref={`/${pathPrefix}/${slugify(slug, id)}`}
        />
      </div>
    );
  }
}


const mapStateToProps = (store, props) => ({
  user: store.user,
  app: store.app,
  artistList: store.artistList,
  releaseList: store.releases.list,
  ...props.mapState(store, props),
});

const mapDispatchToProps = (dispatch, props) => bindActionCreators({
  dispatch,
  setPageNotFound,
  ...props.mapDispatch,
}, dispatch);

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(ReleaseList));
